import { useLocalStorage } from '@/hooks/use-local-storage'
import { CONTROLS_KEY } from '@/lib/constants/local-storage'
import { createContext, useContext, useState } from 'react'

interface ControlsContextType {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const ControlsContext = createContext<ControlsContextType | null>(null)

export function ControlsProvider({
  children,
  initialOpen,
}: {
  children: React.ReactNode
  /** Wins over the saved preference until the first toggle, without saving. */
  initialOpen?: boolean
}) {
  const [saved, save] = useLocalStorage(CONTROLS_KEY, false)
  const [initial, setInitial] = useState(initialOpen)
  const open = initial ?? saved
  const setOpen: React.Dispatch<React.SetStateAction<boolean>> = (action) => {
    setInitial(undefined)
    save(typeof action === 'function' ? action(open) : action)
  }

  return (
    <ControlsContext.Provider value={{ open, setOpen }}>
      <div
        // REMINDER: access the data-expanded state with tailwind via `group-data-[expanded=true]/controls:block`
        // In tailwindcss v4, we could even use `group-data-expanded/controls:block`
        className="group/controls"
        data-expanded={open}>
        {children}
      </div>
    </ControlsContext.Provider>
  )
}

export function useControls() {
  const context = useContext(ControlsContext)

  if (!context) {
    throw new Error('useControls must be used within a ControlsProvider')
  }

  return context as ControlsContextType
}
